/**
 * **조밀한 화면의 디자인 정본** — 선 · 모서리 · 면, 그리고 굵기 규칙.
 *
 * 🔑 이 앱의 화면 대부분은 **표와 목록**이라 MUI 기본 사다리(16px 본문·700 굵기)가 맞지 않는다.
 *   그래서 화면마다 `sx` 에 숫자를 직접 박아 왔고, 같은 「작은 회색 글씨」가 자리마다 달라졌다.
 *   여기가 그 값들의 **한 곳**이다 — 새 숫자를 화면에 적기 전에 여기서 찾는다.
 * ⛔ 여기 없는 값을 화면에서 새로 만들지 마라. 필요하면 **여기에 칸을 더하는** 커밋을 먼저 낸다.
 *
 * ## 굵기 규칙
 * ```
 *   400  본문 · 딸린 말                 — 기본값이다
 *   500  살짝 세우는 것(칸 이름)
 *   600  이름 · 값 · 구역 제목           — 「강조」는 여기서 끝난다
 *   700  화면 제목 하나                  — 그 밖에는 쓰지 않는다
 * ```
 * 🚨 다 굵으면 **아무것도 강조되지 않는다** — 한 화면에서 700 이 95번 쓰이던 것이 출발점이었다.
 *
 * 글자 크기·굵기의 짝은 `core/typography.ts` 의 `ui*` 변종이 쥔다(`<Typography variant="uiNum">`).
 */

/**
 * **간격 사다리** — 여백과 사이 간격은 이 여섯 칸에서만 고른다(MUI `spacing` 단위 · 1 = 8px).
 *
 * ```
 *   0.5  4px   붙어 있는 것(아이콘과 글자 · 칩 안)
 *   1    8px   한 덩어리 안의 줄 사이
 *   1.5  12px  촘촘한 카드의 안쪽 여백
 *   2    16px  카드의 안쪽 여백 · 카드와 카드 사이
 *   3    24px  구역과 구역 사이
 *   4    32px  화면 덩어리 사이
 * ```
 * 🚨 **4px 격자 밖 값을 쓰지 마라** — `0.75`(6px) · `1.25`(10px) · `1.75`(14px) 가 250곳 넘게
 *   섞여 있었다. 한 줄에 6px 과 8px 이 함께 있으면 무엇도 맞아 보이지 않는다.
 * ⚠️ `0.25`(2px)는 **칩 안의 미세 조정**에만 쓴다 — 줄 사이나 카드 여백에 쓰지 않는다.
 * ⛔ 단추·칩의 좌우 여백을 화면에서 주지 마라 — 높이 사다리(`size`)가 함께 정한다.
 */
export const SPACE = {
  tight: 0.5,
  row: 1,
  dense: 1.5,
  card: 2,
  section: 3,
  block: 4,
} as const;

/**
 * **화면 골격의 세로 사이 — 두 칸뿐이다.** 블록(머리·검색칸·조건 줄·띠·탭·결과)은 바깥 여백을 갖지 않고,
 * 사이는 껍데기(`PageZones` · `HubShell` · `MomentPageLayout`)가 이 값으로 준다.
 *
 * ```
 *   top   12px  상단 구역 안 — 머리 · 검색칸 · 조건 줄 · 띠(추적 권유 · 진행 · 알림)
 *   zone  16px  상단 구역 → 본문 구역(탭 · 결과) — 한 단계 넓다
 * ```
 * 🚨 블록 루트에 `mt`/`mb` 를 달지 마라 — 껍데기의 gap 과 **더해져** 조합마다 사이가 달라진다
 *   (띠의 `mb` + 탭 머리의 `pt` 처럼). 게이트 ⑧ EDGE 가 공용 부품 루트를 센다.
 */
export const PAGE_GAP = {
  top: SPACE.dense,
  zone: SPACE.card,
} as const;

/**
 * 가르는 선 — **두 단계뿐**이다.
 * 🔑 `hair` 는 «같은 덩어리 안»을 가른다(줄과 줄), `edge` 는 «덩어리의 바깥»을 두른다(카드).
 * ⚠️ 회색 계열은 하나다(slate) — 무채색 회색과 섞으면 한 화면 안에서 미묘하게 더러워진다.
 */
export const LINE = {
  hair: 'rgba(15, 23, 42, 0.06)',
  edge: 'rgba(15, 23, 42, 0.10)',
} as const;

/**
 * 옅게 까는 면 — 글자가 아니라 **자리**를 표시한다(표 머리·빈 칸·hover).
 * ⚠️ 카드 안에 또 카드를 그리지 마라 — 면 하나로 충분하다.
 */
export const SURFACE = {
  sunken: 'rgba(15, 23, 42, 0.03)',
  hover: 'rgba(15, 23, 42, 0.04)',
} as const;

/**
 * 모서리 — **크기에 따라 셋**이다(+알약).
 * 🔑 작은 것은 덜 둥글다 — 40px 짜리 칸에 16px 모서리를 두면 원처럼 보인다.
 * ⛔ 6 · 13 · 30 같은 어중간한 값을 새로 만들지 마라(그렇게 다섯 갈래가 됐다).
 * ⚠️ **px 로 적는다** — `sx` 의 `borderRadius: 2` 는 16px 이라는 뜻이라(테마 배수), 숫자로 두면
 *   읽는 사람이 8인지 16인지 매번 헤아려야 한다.
 */
export const RADIUS = {
  /** 40px 미만 — 칩 안의 표, 작은 맛보기 컷 */
  sm: '8px',
  /** 40~120px — 단추, 줄, 맛보기 카드 */
  md: '12px',
  /** 그보다 큰 것 — 구역 카드, 대화상자 */
  lg: '16px',
  /** 알약 — 칩·단추 */
  pill: '999px',
} as const;

/**
 * **갈래 고르개 한 꼴** — 알약 껍데기 안에서 하나만 떠 있는 모양.
 *
 * 🔑 글자형(`SegmentTabs`)과 아이콘형(보기 모드 단추)이 **같은 것**이라야 한 서비스로 읽힌다.
 *   둘이 따로 그려져 있어서 면 색(`grey.100` 대 `SURFACE.sunken`)과 그림자가 어긋나 있었다.
 * ⚠️ 껍데기는 **제 너비만 쓴다**(`alignSelf`) — 세로 `Stack` 안의 기본은 «늘리기»라 알약이
 *   화면 폭으로 늘어난다.
 * ⛔ 이 모양을 화면에서 새로 그리지 마라 — 보기를 바꾸면 갈래 수와 상관없이 이것이고, 조건을 걸면 칩이다.
 */
export const SEGMENT = {
  shell: {
    display: 'inline-flex',
    alignSelf: 'flex-start',
    p: 0.375,
    borderRadius: RADIUS.pill,
    bgcolor: SURFACE.sunken,
  },
  /** 고른 칸은 «떠 있고», 나머지는 면에 잠겨 있다 */
  item: (on: boolean) => ({
    borderRadius: RADIUS.pill,
    color: on ? 'text.primary' : 'text.secondary',
    bgcolor: on ? 'background.paper' : 'transparent',
    boxShadow: on ? `0 1px 2px ${LINE.edge}` : 'none',
    '&:hover': { bgcolor: on ? 'background.paper' : SURFACE.hover },
  }),
} as const;

/**
 * **그림자 — 두 칸뿐**이다.
 *
 * 🔑 그림자는 «떠 있다»는 말이다. 카드마다 그림자를 두면 구역들이 서로 떠 보여 층이 사라진다 —
 *   면 위의 덩어리는 **테두리 하나**로 가르고(`divider`), 그림자는 **정말 떠 있는 것**만 쓴다.
 * ⚠️ 서른 갈래가 섞여 있었다(`0 4px 10px rgba(0,0,0,.08)` · `0 20px 40px …` · `0 8px 32px …`).
 *   퍼짐과 알파가 제각각이면 같은 화면 안에서 어떤 것이 위인지 읽히지 않는다.
 * ⛔ 새 그림자를 화면에서 만들지 마라.
 */
export const SHADOW = {
  /** 면에서 살짝 뜬 것 — 갈래 고르개의 고른 칸, 끌어 옮기는 카드 */
  raised: `0 1px 2px ${LINE.edge}`,
  /** 본문 위에 뜬 것 — 메뉴 · 팝오버 · 대화상자 */
  overlay: '0 8px 24px rgba(15, 23, 42, 0.12)',
} as const;

/**
 * **잠긴 것의 표 — 아이콘 하나**다.
 *
 * 🔑 「이 칸은 상위 플랜에서 열린다」는 말은 화면마다 같은 그림이어야 한다. 한때 다섯 갈래가
 *   섞여 있었다 — `solar:lock-keyhole-minimalistic-linear` · `solar:lock-linear` ·
 *   `solar:lock-keyhole-minimalistic-bold` · 글자로 박은 `🔒` 둘. 같은 뜻인데 자리마다 달랐다.
 * ⚠️ **이모지로 적지 마라** — 글꼴을 타서 기기마다 색·크기가 다르고, 글자 크기를 따라
 *   커지므로 칩 안에서 줄이 울퉁불퉁해진다. `<Iconify icon={LOCK_ICON} width={14} />` 로 세운다.
 * ⛔ 「푼다」는 뜻은 이 값이 아니다 — 자물쇠가 열린 그림(`solar:lock-unlocked-linear`)은 따로 쓴다.
 */
export const LOCK_ICON = 'solar:lock-keyhole-minimalistic-linear';

/**
 * **좁은 폭의 한국어 줄바꿈 — 세 벌**이다. 유도 띠·안내 카드처럼 모바일에서 여러 줄로 접히는 글에 건다.
 *
 * 🔑 `title` 은 줄 길이를 고르게(`balance`), `body` 는 마지막 줄에 한 낱말만 떨어지지 않게(`pretty`) 한다.
 *   둘 다 어절 중간에서 끊지 않는다(`keep-all`) — 「올/린」처럼 낱말이 갈리면 문장이 안 읽힌다.
 * 🔑 `keep` 은 쪼개면 뜻이 갈리는 짝(「최근 365일」·「6,000건」)을 한 덩어리로 묶는다.
 * ⚠️ `overflowWrap: 'break-word'` 는 띄어쓰기 없는 긴 영문·주소가 칸을 뚫지 않게 하는 마지막 그물이다 —
 *   `anywhere` 와 달리 최소 폭을 줄이지 않아 옆 단추를 밀어내지 않는다.
 */
export const TEXT_WRAP = {
  title: { wordBreak: 'keep-all', overflowWrap: 'break-word', textWrap: 'balance' },
  body: { wordBreak: 'keep-all', overflowWrap: 'break-word', textWrap: 'pretty' },
  keep: { whiteSpace: 'nowrap' },
} as const;
